// The Templates view (ADR-0051 Stage 2): the single place to fully edit a
// template — name, worth, recurrence, who-can-do, visibility, labels, and
// timed show/hide. Built with Material 3 web components (md-*), the first
// fully-Material view; the recurrence→spawn behavior is automatic server-side
// (ADR-0050). Editing is admin-only (PATCH is), so this is a drawer
// destination gated to admins in main.ts.
import '@material/web/textfield/outlined-text-field.js';
import '@material/web/select/outlined-select.js';
import '@material/web/select/select-option.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';
import '@material/web/chips/chip-set.js';
import '@material/web/chips/filter-chip.js';

import { api, type Member, type Tag, type Template } from '../api.js';
import { ce, el, clear, fmtMinutes, type ChipEl, type ValueEl } from '../dom.js';
import { t, type MessageKey } from '../i18n.js';
import { eligibilityPicker, type EligibilityOption, type EligibilityRefsData } from './authoring.js';


const FREQ_OPTS: [string, MessageKey][] = [
  ['', 'adminOneTime'],
  ['daily', 'freqDaily'],
  ['weekly', 'freqWeekly'],
  ['monthly', 'freqMonthly'],
];
const VIS_OPTS: [string, MessageKey][] = [
  ['everyone', 'tasksVisibilityEveryone'],
  ['assignee', 'tasksVisibilityAssignee'],
  ['admins', 'tasksVisibilityAdmins'],
];

function select(label: string, field: string, opts: [string, MessageKey][], current: string): ValueEl {
  const sel = ce('md-outlined-select', { label, 'data-field': field }, ...opts.map(([value, key]) =>
    ce('md-select-option', { value, ...(value === current ? { selected: true } : {}) }, ce('div', { slot: 'headline' }, t(key))),
  )) as ValueEl;
  return sel;
}

export function renderTemplates(): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });
  const newHost = el('div', { class: 'templates-new' });
  const list = el('div', { class: 'templates-list' });

  let eligOptions: EligibilityOption[] = [];
  let catTags: { id: number; name: string }[] = [];

  function editor(tpl: Template | null, onDone: () => void, onCancel: () => void): HTMLElement {
    const name = ce('md-outlined-text-field', { label: t('composeNameLabel'), value: tpl?.title ?? '', 'data-field': 'name' }) as ValueEl;
    const worth = ce('md-outlined-text-field', {
      label: t('composeWorth'), type: 'number', min: '1', 'suffix-text': t('composeMinSuffix'), value: String(tpl?.baseMinutes ?? 5), 'data-field': 'worth',
    }) as ValueEl;
    const repeat = select(t('templatesRepeat'), 'repeat', FREQ_OPTS, tpl?.recurrence?.freq ?? '');
    const visibility = select(t('composeVisibility'), 'visibility', VIS_OPTS, tpl?.visibility ?? 'everyone');
    // Manual payout multiplier (ADR-0008) — nobody likes scrubbing the oven,
    // so it can be worth more than its minutes. This used to be settable only
    // at creation time in the old admin card; here it stays editable.
    const mult = ce('md-outlined-text-field', {
      label: t('templatesMultiplier'), type: 'number', min: '0.1', max: '10', step: '0.1',
      value: String(tpl?.manualMultiplier ?? 1), 'data-field': 'multiplier',
    }) as ValueEl;

    let refs: EligibilityRefsData = {
      memberIds: [...(tpl?.eligibilityRefs?.memberIds ?? [])],
      tagIds: [...(tpl?.eligibilityRefs?.tagIds ?? [])],
    };
    const picker = eligibilityPicker({
      options: eligOptions,
      selected: refs,
      labels: { everyone: t('eligEveryone'), typePlaceholder: t('eligType'), groupTag: t('eligGroupTag'), noMatches: t('eligNoMatches'), remove: t('eligRemove') },
      onChange: (next) => { refs = next; },
    });

    const labelChips = catTags.map((tag) =>
      ce('md-filter-chip', { label: tag.name, ...(tpl?.tagIds?.includes(tag.id) ? { selected: true } : {}) }) as ChipEl,
    );
    const chipSet = ce('md-chip-set', {}, ...labelChips);

    const showAt = ce('md-outlined-text-field', { label: t('tasksModalShowAt'), type: 'time', value: tpl?.showAtTime ?? '' }) as ValueEl;
    const hideBy = ce('md-outlined-text-field', { label: t('tasksModalHideBy'), type: 'time', value: tpl?.hideByTime ?? '' }) as ValueEl;

    const editError = el('p', { class: 'error' });

    function collect(): Record<string, unknown> | null {
      const title = name.value.trim();
      const baseMinutes = Number(worth.value);
      if (!title || !baseMinutes) { editError.textContent = t('tasksNameMinutesRequired'); return null; }
      const tagIds = catTags.filter((_, i) => labelChips[i]!.selected).map((tg) => tg.id);
      return {
        title,
        baseMinutes,
        manualMultiplier: Number(mult.value) || 1,
        recurrence: repeat.value ? { freq: repeat.value } : null,
        eligibilityRefs: refs.memberIds.length || refs.tagIds.length ? refs : null,
        visibility: visibility.value || 'everyone',
        tagIds,
        showAtTime: showAt.value || null,
        hideByTime: hideBy.value || null,
      };
    }

    function save(): void {
      const body = collect();
      if (!body) return;
      const req = tpl
        ? api.patch(`/api/templates/${tpl.id}`, body)
        : api.post('/api/templates', body);
      req.then(() => onDone()).catch((err: Error) => { editError.textContent = err.message; });
    }

    const actions = el('div', { class: 'templates-actions' });
    const saveBtn = ce('md-filled-button', { type: 'button' }, t('adminSave'));
    saveBtn.addEventListener('click', save);
    const cancelBtn = ce('md-text-button', { type: 'button' }, t('tasksCancel'));
    cancelBtn.addEventListener('click', onCancel);
    actions.append(saveBtn, cancelBtn);
    if (tpl) {
      const retireBtn = ce('md-text-button', { type: 'button', class: 'danger' }, t('adminRetire'));
      retireBtn.addEventListener('click', () => {
        api.patch(`/api/templates/${tpl.id}`, { active: false }).then(() => onDone()).catch((err: Error) => { editError.textContent = err.message; });
      });
      actions.append(retireBtn);
    }

    return el('div', { class: 'template-editor' },
      el('div', { class: 'templates-fields' }, name, worth, mult, repeat, visibility),
      el('label', { class: 'templates-field-label' }, t('composeWhoCanDo')), picker,
      el('label', { class: 'templates-field-label' }, t('tasksModalLabels')),
      catTags.length ? chipSet : el('span', { class: 'muted' }, t('tasksNoLabels')),
      el('div', { class: 'templates-fields templates-timing' }, showAt, hideBy),
      editError,
      actions,
    );
  }

  function templateRow(tpl: Template): HTMLElement {
    const row = el('div', { class: 'card template-row', 'data-template': tpl.title });
    function showDisplay(): void {
      clear(row);
      const rec = tpl.recurrence ? t(({ daily: 'freqDaily', weekly: 'freqWeekly', monthly: 'freqMonthly' } as const)[tpl.recurrence.freq]) : t('adminOneTime');
      // A multiplier of 1 is the norm; only call it out when it isn't.
      const meta = [fmtMinutes(tpl.baseMinutes), rec, ...(tpl.manualMultiplier === 1 ? [] : [`×${tpl.manualMultiplier}`])];
      const editBtn = ce('md-text-button', { type: 'button', 'data-edit': tpl.title }, t('adminEdit'));
      editBtn.addEventListener('click', showEdit);
      row.append(
        el('div', { class: 'template-row-head' },
          el('div', {}, el('div', { class: 'title' }, tpl.title), el('div', { class: 'meta' }, meta.join(' · '))),
          editBtn,
        ),
      );
    }
    function showEdit(): void {
      clear(row);
      row.append(el('div', { class: 'title' }, tpl.title), editor(tpl, load, showDisplay));
    }
    showDisplay();
    return row;
  }

  function renderNew(): void {
    clear(newHost);
    const openBtn = ce('md-filled-button', { type: 'button', 'data-new-template': 'true' }, t('templatesNew'));
    openBtn.addEventListener('click', () => {
      clear(newHost);
      newHost.append(el('div', { class: 'card template-row' }, editor(null, load, renderNew)));
    });
    newHost.append(openBtn);
  }

  function load(): void {
    error.textContent = '';
    Promise.all([
      api.get<{ templates: Template[] }>('/api/templates'),
      api.get<{ members?: Member[] }>('/api/state'),
      api.get<{ tags: Tag[] }>('/api/tags'),
    ])
      .then(([{ templates }, { members }, { tags }]) => {
        eligOptions = [
          ...tags.filter((x) => x.kind === 'eligibility').map((g) => ({ kind: 'group' as const, id: g.id, name: g.name })),
          ...(members ?? []).map((m) => ({ kind: 'member' as const, id: m.id, name: m.name, color: m.color })),
        ];
        catTags = tags.filter((x) => x.kind === 'category').map((x) => ({ id: x.id, name: x.name }));
        renderNew();
        clear(list);
        const active = templates.filter((tp) => tp.active);
        if (active.length === 0) list.append(el('p', { class: 'muted' }, t('templatesEmpty')));
        for (const tpl of active) list.append(templateRow(tpl));
      })
      .catch((err: Error) => { error.textContent = err.message; });
  }

  root.append(error, newHost, list);
  load();
  return root;
}
